import type { Polygon } from '@tsr/shared-types';

const METERS_PER_DEG_LAT = 111_320;
const ARC_STEPS = 24;
const FULL_CIRCLE_EPSILON = 0.5; // degrees — treat fov >=359.5 as full 360

const toRad = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Equirectangular (flat-earth) approximation. Accurate to roughly ±1% at
 * a 1 km radius, degrading rapidly beyond ~100 km. Fine for camera FOV
 * cones in the 100 m – 5 km range; do not use for cross-province
 * geometry.
 */
function metersToDegrees(lat: number, dN: number, dE: number): [number, number] {
  const metersPerDegLon = METERS_PER_DEG_LAT * Math.cos(toRad(lat));
  return [dE / metersPerDegLon, dN / METERS_PER_DEG_LAT];
}

/**
 * Phase A stub. Flat-earth approximation: builds a triangular fan polygon
 * representing the camera FOV cone. Kept for the legacy positional-arg
 * call sites; new code should prefer cameraConePolygon.
 */
export function cameraFovPolygon(
  lat: number,
  lon: number,
  headingDeg: number,
  fovDeg: number,
  rangeM: number,
): Polygon {
  return cameraConePolygon({ lat, lon, headingDeg, fovDeg, rangeM, segments: ARC_STEPS });
}

export interface CameraConeInput {
  lat: number;
  lon: number;
  headingDeg: number;
  fovDeg: number;
  rangeM: number;
  /** Number of arc segments around the curved edge. Default 16. */
  segments?: number;
}

/**
 * GeoJSON Polygon for a camera's footprint cone, anchored at (lon, lat).
 * Equirectangular projection (see `metersToDegrees`) — sufficient for
 * traffic-camera ranges (≤ ~5 km). Three special cases:
 *   - `fovDeg ≥ 359.5` → full circle around the camera (no apex vertex).
 *   - `rangeM === 0`   → degenerate polygon collapsed to the apex point
 *                         (still a valid 4-vertex closed ring).
 *   - otherwise        → apex + arc sweep + back to apex (segments + 3
 *                         vertices total: apex, segments+1 arc points, apex).
 */
export function cameraConePolygon(input: CameraConeInput): Polygon {
  const { lat, lon, headingDeg, fovDeg, rangeM, segments = 16 } = input;
  if (fovDeg <= 0 || fovDeg > 360) throw new Error(`fovDeg out of range: ${fovDeg}`);
  if (rangeM < 0) throw new Error(`rangeM must be >= 0: ${rangeM}`);
  if (segments < 4) throw new Error(`segments must be >= 4: ${segments}`);

  if (rangeM === 0) {
    return {
      type: 'Polygon',
      coordinates: [[
        [lon, lat],
        [lon, lat],
        [lon, lat],
        [lon, lat],
      ]],
    };
  }

  const fullCircle = fovDeg >= 360 - FULL_CIRCLE_EPSILON;
  const arcSpan = fullCircle ? 360 : fovDeg;
  const startBearing = fullCircle ? 0 : headingDeg - fovDeg / 2;
  const stepDeg = arcSpan / segments;

  const ring: Array<[number, number]> = fullCircle ? [] : [[lon, lat]];
  const stopAt = fullCircle ? segments : segments;
  for (let i = 0; i <= stopAt; i++) {
    const bearing = toRad(startBearing + i * stepDeg);
    const dN = rangeM * Math.cos(bearing);
    const dE = rangeM * Math.sin(bearing);
    const [dLon, dLat] = metersToDegrees(lat, dN, dE);
    ring.push([lon + dLon, lat + dLat]);
  }
  if (!fullCircle) ring.push([lon, lat]);
  // ensure closed (first === last); for full circle the loop already closes
  if (fullCircle) ring.push(ring[0]!);

  return { type: 'Polygon', coordinates: [ring] };
}

export interface CameraCoverageInput {
  lat: number;
  lon: number;
  rangeM: number;
  /** Number of arc segments around the circle. Default 16. */
  segments?: number;
}

/**
 * Omnidirectional coverage disc — a closed circle of `rangeM` radius
 * around the camera apex. Use when the upstream source doesn't report
 * a real heading, so we don't mislead operators with a directional cone.
 * Implemented as a thin wrapper over cameraConePolygon(fov=360) for
 * consistent geometry semantics.
 */
export function cameraCoveragePolygon(input: CameraCoverageInput): Polygon {
  return cameraConePolygon({
    lat: input.lat,
    lon: input.lon,
    headingDeg: 0,
    fovDeg: 360,
    rangeM: input.rangeM,
    segments: input.segments,
  });
}
